import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";

import { WaitingListProduct } from "@/api/generatedTypes";
import WaitingListApi from "@/api/WaitingListApi";

import { useToastHandlers } from "../toaster";

export const useWaitingListHandlers = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToastHandlers();
  const router = useRouter();

  const waitingListQuery = useQuery(
    ["waiting-list"],
    WaitingListApi.waitingList
  );

  const showToast = (currentProductSize: WaitingListProduct, text: string) => {
    addToast({
      photo: currentProductSize.thumbnail || "",
      title: `${currentProductSize.name} (${currentProductSize.size.name})`,
      text: text,
      variant: "product",
      clickHandler: () => router.push("/waiting-list"),
    });
  };

  const addToWaitingListMutation = useMutation(
    ({ scmId }: { scmId: string }) => WaitingListApi.addToWaitingList(scmId),
    {
      onSuccess(data, variables) {
        queryClient.setQueryData(["waiting-list"], data);

        const currentProductSize = data.data.find(
          (item) => item.size.id === variables.scmId
        );
        if (currentProductSize) {
          showToast(currentProductSize, "оформлена подписка на товар");
        }
      },
    }
  );

  const deleteFromWaitingListMutation = useMutation(
    ({ id }: { id: number }) => WaitingListApi.deleteFromWaitingList(id),
    {
      onSuccess(data, variables) {
        const currentProductSize = waitingListQuery.data?.data.find(
          (item) => item.id === variables.id
        );
        if (currentProductSize) {
          showToast(currentProductSize, "подписка на товар отменена");
        }
        queryClient.setQueryData(["waiting-list"], data);
      },
    }
  );

  const deleteBatchMutation = useMutation(
    ({ waitingListItemsIds }: { waitingListItemsIds: number[] }) =>
      Promise.all(
        waitingListItemsIds.map((id) =>
          WaitingListApi.deleteFromWaitingList(id)
        )
      ),
    {
      onSettled() {
        waitingListQuery.refetch();
      },
    }
  );

  const waitingList = waitingListQuery.data?.data || [];
  const isLoading =
    waitingListQuery.isLoading ||
    addToWaitingListMutation.isLoading ||
    deleteFromWaitingListMutation.isLoading ||
    deleteBatchMutation.isLoading;

  const addToWaitingList = (sizeId: string) => {
    addToWaitingListMutation.mutate({ scmId: sizeId });
  };

  const deleteFromWaitingList = (waitingListId: number) => {
    deleteFromWaitingListMutation.mutate({ id: waitingListId });
  };

  const batchDelete = () => {
    const waitingListItemsIds = waitingList?.map((item) => item.id);
    deleteBatchMutation.mutate({ waitingListItemsIds });
  };

  return {
    addToWaitingList,
    waitingList,
    deleteFromWaitingList,
    batchDelete,
    isLoading,
  };
};
